import { Block } from 'baseui/block';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { styled } from 'baseui';
import React, { PureComponent, Fragment } from 'react';
import type { QnAStateType } from '../../reducers';
import { questionTypes } from '../../reducers/constants';
import { getFollowUpQuestionIdsByAnswers, getQuestionById, getUserAnswersByQuestionId } from '../../reducers/selectors';
import { gutter, boxRadius } from '../../utils/styles';
import Answers from './Answers';
import QuestionTitle from './QuestionTitle';
import { QuestionValidation } from './question-validation';
import { Button, KIND as BUTTON_KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { Popover } from 'baseui/popover';
const Item = styled<typeof Block, any>(Block, ({
  $theme
}) => ({
  display: 'block',
  padding: gutter,
  margin: '0 0 8px',
  borderRadius: boxRadius,
  background: $theme.colors.backgroundPrimary,
  color: $theme.colors.primaryA,
  '@keyframes qna-focus-pulse': {
    '0%, 100%': {
      boxShadow: `0 0 0 ${$theme.sizing.scale0} ${$theme.colors.borderAccent}1A, 0 0 ${$theme.sizing.scale200} ${$theme.colors.borderAccent}0D`,
      backgroundColor: `${$theme.colors.borderAccent}05`
    },
    '50%': {
      boxShadow: `0 0 0 ${$theme.sizing.scale200} ${$theme.colors.borderAccent}66, 0 0 ${$theme.sizing.scale600} ${$theme.colors.borderAccent}33`,
      backgroundColor: `${$theme.colors.borderAccent}0D`
    }
  }
}));
const PopoverContent = styled('div', ({
  $theme
}) => ({
  padding: $theme.sizing.scale500,
  maxWidth: $theme.sizing.scale4800
}));
const PopoverMessage = styled('div', ({
  $theme
}) => ({
  marginBottom: $theme.sizing.scale400,
  color: $theme.colors.contentPrimary
}));
const PopoverActions = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale300
}));
type Props = {
  state: QnAStateType;
  questionId: string;
  updateAnswers: Function;
  selectAnswer: Function;
  removeAnswer: Function;
  shouldForceUpdateRTEAnswer?: boolean;
  setForceUpdateRTEAnswer?: (prevValue: boolean) => void;
  updateNote?: Function | null;
  showAnswerAsSelectLimit?: number | null;
  isComposeMode?: boolean;
  enableAutoFocusForRTE?: boolean;
  readOnly?: boolean;
  registerQuestionNode?: (questionId: string, node: HTMLElement | null) => void;
};
type WarningConfig = {
  message: string;
  confirmText: string;
  cancelText: string;
};
type State = {
  pendingRemovePayload: Record<string, unknown> | null;
  pendingSelectPayload: Record<string, unknown> | null;
};
export default class Question extends PureComponent<Props, State> {
  private questionNodeRef = React.createRef<HTMLLIElement>();
  state: State = {
    pendingRemovePayload: null,
    pendingSelectPayload: null
  };
  getWarningConfig = (): WarningConfig | null => {
    const {
      state: qnaState,
      questionId
    } = this.props;
    const question = getQuestionById(qnaState, questionId);
    const tags: string[] = get(question, 'tags') ?? [];
    const messageTag = tags.find(tag => tag.startsWith('warning_message:'));
    if (!messageTag) return null;
    const message = messageTag.substring('warning_message:'.length).trim();
    const confirmTag = tags.find(tag => tag.startsWith('warning_confirm_text:'));
    const confirmText = confirmTag ? confirmTag.substring('warning_confirm_text:'.length).trim() : 'Yes, Continue';
    const cancelTag = tags.find(tag => tag.startsWith('warning_cancel_text:'));
    const cancelText = cancelTag ? cancelTag.substring('warning_cancel_text:'.length).trim() : 'Cancel';
    return {
      message,
      confirmText,
      cancelText
    };
  };
  handleSelectAnswer = (payload: Record<string, unknown>) => {
    const {
      state: qnaState,
      questionId
    } = this.props;
    if (this.getWarningConfig()) {
      const question = getQuestionById(qnaState, questionId);
      if (get(question, 'type') === questionTypes.select && get(question, 'limit') === 1) {
        const userAnswers = getUserAnswersByQuestionId(qnaState, questionId);
        if (userAnswers.some(a => a !== '')) {
          this.setState({
            pendingSelectPayload: payload
          });
          return;
        }
      }
    }
    this.props.selectAnswer(payload);
  };
  handleRemoveAnswer = (payload: Record<string, unknown>) => {
    const {
      state: qnaState,
      questionId
    } = this.props;
    if (this.getWarningConfig()) {
      const userAnswers = getUserAnswersByQuestionId(qnaState, questionId);
      if (userAnswers.some(a => a !== '')) {
        this.setState({
          pendingRemovePayload: payload
        });
        return;
      }
    }
    this.props.removeAnswer(payload);
  };
  handleConfirmAction = () => {
    const {
      pendingRemovePayload,
      pendingSelectPayload
    } = this.state;
    if (pendingSelectPayload) {
      this.props.selectAnswer(pendingSelectPayload);
      this.setState({
        pendingSelectPayload: null
      });
    } else if (pendingRemovePayload) {
      this.props.removeAnswer(pendingRemovePayload);
      this.setState({
        pendingRemovePayload: null
      });
    }
  };
  handleCancelAction = () => {
    this.setState({
      pendingRemovePayload: null,
      pendingSelectPayload: null
    });
  };
  componentDidMount() {
    const {
      selectAnswer,
      state,
      questionId,
      registerQuestionNode
    } = this.props;
    const question = getQuestionById(state, questionId);
    const answers = getUserAnswersByQuestionId(state, questionId);
    if (question && isEmpty(answers)) {
      selectAnswer({
        qnaState: state,
        questionId,
        question: get(question, 'question'),
        type: get(question, 'type'),
        limit: 0,
        answer: ''
      });
    }
    if (registerQuestionNode && this.questionNodeRef.current) {
      registerQuestionNode(questionId, this.questionNodeRef.current);
    }
  }
  render() {
    const {
      state,
      questionId,
      updateAnswers,
      selectAnswer,
      removeAnswer,
      shouldForceUpdateRTEAnswer,
      setForceUpdateRTEAnswer,
      updateNote,
      showAnswerAsSelectLimit,
      isComposeMode,
      enableAutoFocusForRTE,
      readOnly = false,
      registerQuestionNode
    } = this.props;
    const question = getQuestionById(state, questionId);
    const followUpQuestionIds = getFollowUpQuestionIdsByAnswers(state, questionId);
    const isReadOnlyQuestion = readOnly || (get(question, 'tags') ?? []).includes('read_only');
    const isHidden = (get(question, 'tags') ?? []).includes('warning_hidden');
    if (!question || isHidden) {
      return null;
    }
    const warningConfig = this.getWarningConfig();
    return <Fragment>
        <Item as="li" ref={this.questionNodeRef}>
          <QuestionTitle question={question}></QuestionTitle>
          <QuestionValidation qnaState={state} questionId={questionId} />
          {warningConfig ? <Popover isOpen={!!this.state.pendingRemovePayload || !!this.state.pendingSelectPayload} showArrow onClickOutside={this.handleCancelAction} content={() => <PopoverContent>
                  <PopoverMessage>{warningConfig.message}</PopoverMessage>
                  <PopoverActions>
                    <Button kind={BUTTON_KIND.primary} size={BUTTON_SIZE.compact} onClick={this.handleConfirmAction}>
                      {warningConfig.confirmText}
                    </Button>
                    <Button kind={BUTTON_KIND.tertiary} size={BUTTON_SIZE.compact} onClick={this.handleCancelAction}>
                      {warningConfig.cancelText}
                    </Button>
                  </PopoverActions>
                </PopoverContent>}>
              <div>
                <Answers
            // @ts-expect-error todo(ts-migration) TS2322 Type '{ question: any; state: QnAStateType; selectAnswer: Function; removeAnswer: Function; updateNote: Function; showAnswerAsSelectLimit: number; }' is not assignable to type 'IntrinsicAttributes & I...
            question={question} state={state} updateAnswers={updateAnswers} selectAnswer={this.handleSelectAnswer} removeAnswer={this.handleRemoveAnswer} updateNote={updateNote} shouldForceUpdateRTEAnswer={shouldForceUpdateRTEAnswer} setForceUpdateRTEAnswer={setForceUpdateRTEAnswer} showAnswerAsSelectLimit={showAnswerAsSelectLimit} isComposeMode={isComposeMode} enableAutoFocusForRTE={enableAutoFocusForRTE} readOnly={isReadOnlyQuestion} />
              </div>
            </Popover> : <Answers
        // @ts-expect-error todo(ts-migration) TS2322 Type '{ question: any; state: QnAStateType; selectAnswer: Function; removeAnswer: Function; updateNote: Function; showAnswerAsSelectLimit: number; }' is not assignable to type 'IntrinsicAttributes & I...
        question={question} state={state} updateAnswers={updateAnswers} selectAnswer={selectAnswer} removeAnswer={removeAnswer} updateNote={updateNote} shouldForceUpdateRTEAnswer={shouldForceUpdateRTEAnswer} setForceUpdateRTEAnswer={setForceUpdateRTEAnswer} showAnswerAsSelectLimit={showAnswerAsSelectLimit} isComposeMode={isComposeMode} enableAutoFocusForRTE={enableAutoFocusForRTE} readOnly={isReadOnlyQuestion} />}
        </Item>
        {followUpQuestionIds.map(id => {
        return <Question key={`question-${id}`} questionId={id} state={state} updateAnswers={updateAnswers} shouldForceUpdateRTEAnswer={shouldForceUpdateRTEAnswer} setForceUpdateRTEAnswer={setForceUpdateRTEAnswer} selectAnswer={selectAnswer} removeAnswer={removeAnswer} updateNote={updateNote} showAnswerAsSelectLimit={showAnswerAsSelectLimit} isComposeMode={isComposeMode} enableAutoFocusForRTE={enableAutoFocusForRTE} registerQuestionNode={registerQuestionNode} />;
      })}
      </Fragment>;
  }
}